import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, Mail, Smartphone, KeyRound, LogOut,
  CheckCircle2, AlertTriangle, Copy, Loader2, Eye,
} from "lucide-react";

/**
 * /admin/security — Admin self-service security center.
 *
 * Sections:
 *   1. Account status (email verified? 2FA on? backup admin?)
 *   2. Change password (current + new)
 *   3. Change email (current pw + new email → email-link verification)
 *   4. Two-factor authentication (enrol / disable)
 *   5. Sessions — revoke all
 *   6. Recent activity (audit log)
 */
export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [s, a] = await Promise.all([
        api.get("/auth/admin/security-status"),
        api.get("/auth/admin/audit-log"),
      ]);
      setStatus(s.data);
      setAudit(a.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  if (loading) return (
    <div className="p-10 text-center" data-testid="admin-security-loading">
      <Loader2 className="w-8 h-8 animate-spin mx-auto text-brand-chili" />
    </div>
  );

  return (
    <div className="container-x py-10 max-w-3xl space-y-8" data-testid="admin-security-page">
      <div>
        <h1 className="font-serif text-3xl text-brand-dark">Security</h1>
        <p className="text-sm text-brand-muted mt-1">Manage your admin account credentials and 2FA.</p>
      </div>

      <StatusCard status={status} />
      <ChangePasswordCard />
      <ChangeEmailCard currentEmail={status?.email} />
      <TwoFACard status={status} onChange={refresh} />
      <SessionsCard onRevoke={refresh} />
      <AuditCard items={audit} />

      {/* iter98 / Session E — Employee Admin management (separate from
          Super Admin controls above).  Only visible to super_admin. */}
      <EmployeeAdminActivityCard />
      <EmployeeAdminChangePasswordCard />
      <EmployeeAdminChangeEmailCard />
    </div>
  );
}

function StatusRow({ label, ok, hint }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-brand-line last:border-0">
      <div className="flex items-center gap-2">
        {ok
          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />
          : <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={2} />}
        <span className="text-sm text-brand-dark">{label}</span>
      </div>
      <span className="text-xs text-brand-muted">{hint}</span>
    </div>
  );
}

function StatusCard({ status }) {
  return (
    <section className="bg-white rounded-2xl border border-brand-line p-6" data-testid="security-status-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
        <h2 className="font-serif text-xl text-brand-dark">Account status</h2>
      </div>
      <StatusRow ok={status?.email_verified}
        label={`Email verified: ${status?.email}`}
        hint={status?.email_verified ? "Verified" : "Click 'Resend verification' below"} />
      <StatusRow ok={status?.is_2fa_enabled}
        label="Two-factor authentication"
        hint={status?.is_2fa_enabled ? `${status.recovery_codes_remaining || 0} recovery codes left` : "Not enabled"} />
      <StatusRow ok={!status?.is_backup_admin}
        label="Role"
        hint={status?.is_backup_admin ? "Backup admin (read-only safety net)" : "Primary admin"} />
    </section>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/admin/change-password", { current_password: current, new_password: pwd });
      toast.success("Password updated. All other sessions signed out.");
      setCurrent(""); setPwd(""); setPwd2("");
      // Token rotated → re-auth needed (session_version changed)
      localStorage.removeItem("sdf_token");
      setTimeout(() => { window.location.href = "/login"; }, 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-brand-line p-6" data-testid="security-change-pwd-card">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
        <h2 className="font-serif text-xl text-brand-dark">Change password</h2>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input type="password" className="input-field" placeholder="Current password"
          value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required
          data-testid="security-current-pwd" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="password" className="input-field" placeholder="New password (10+ chars)"
            value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="new-password" required
            data-testid="security-new-pwd" />
          <input type="password" className="input-field" placeholder="Confirm new password"
            value={pwd2} onChange={(e) => setPwd2(e.target.value)} autoComplete="new-password" required
            data-testid="security-confirm-pwd" />
        </div>
        <button type="submit" disabled={submitting} className="btn-primary" data-testid="security-change-pwd-submit">
          {submitting ? "Updating…" : "Update password"}
        </button>
      </form>
    </section>
  );
}

function ChangeEmailCard({ currentEmail }) {
  const [pwd, setPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/admin/request-email-change", { current_password: pwd, new_email: newEmail });
      toast.success("Verification links sent to both your current and new email.");
      setPwd(""); setNewEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-brand-line p-6" data-testid="security-change-email-card">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
        <h2 className="font-serif text-xl text-brand-dark">Change email</h2>
      </div>
      <p className="text-xs text-brand-muted mb-3">Currently: <span className="font-medium text-brand-dark">{currentEmail}</span></p>
      <form onSubmit={submit} className="space-y-3">
        <input type="password" className="input-field" placeholder="Current password"
          value={pwd} onChange={(e) => setPwd(e.target.value)} required autoComplete="current-password"
          data-testid="security-email-pwd" />
        <input type="email" className="input-field" placeholder="New email address"
          value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required
          data-testid="security-new-email" />
        <button type="submit" disabled={submitting} className="btn-primary" data-testid="security-change-email-submit">
          {submitting ? "Sending links…" : "Send verification links"}
        </button>
      </form>
    </section>
  );
}

function TwoFACard({ status, onChange }) {
  const [enroll, setEnroll] = useState(null);
  const [otp, setOtp] = useState("");
  const [disablePwd, setDisablePwd] = useState("");
  const [disableOtp, setDisableOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const startEnroll = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/admin/2fa/enroll");
      setEnroll(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  const finishEnroll = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/admin/2fa/verify-enroll", { otp });
      toast.success("Two-factor authentication enabled.");
      setEnroll(null); setOtp("");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  const disable = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/admin/2fa/disable", { current_password: disablePwd, otp: disableOtp });
      toast.success("2FA disabled.");
      setDisablePwd(""); setDisableOtp("");
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-brand-line p-6" data-testid="security-2fa-card">
      <div className="flex items-center gap-2 mb-3">
        <Smartphone className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
        <h2 className="font-serif text-xl text-brand-dark">Two-factor authentication</h2>
      </div>

      {status?.is_2fa_enabled ? (
        <>
          <p className="text-sm text-emerald-700 mb-4">
            <CheckCircle2 className="inline w-4 h-4 mr-1" /> 2FA is ON. {status.recovery_codes_remaining || 0} recovery codes remaining.
          </p>
          <form onSubmit={disable} className="space-y-3 border-t border-brand-line pt-4">
            <h3 className="text-sm font-medium text-brand-dark">Disable 2FA</h3>
            <input type="password" className="input-field" placeholder="Current password"
              value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} required
              data-testid="security-2fa-disable-pwd" />
            <input type="text" inputMode="text" className="input-field font-mono"
              placeholder="6-digit code or recovery code"
              value={disableOtp} onChange={(e) => setDisableOtp(e.target.value)} required
              data-testid="security-2fa-disable-otp" />
            <button type="submit" disabled={submitting}
              className="btn-secondary border-amber-400 text-amber-700"
              data-testid="security-2fa-disable-submit">
              {submitting ? "Disabling…" : "Disable 2FA"}
            </button>
          </form>
        </>
      ) : enroll ? (
        <form onSubmit={finishEnroll} className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <img src={enroll.qr_data_url} alt="2FA QR" className="w-44 h-44 border border-brand-line rounded" data-testid="security-2fa-qr" />
            <div className="flex-1 text-xs">
              <p className="text-brand-muted mb-1">Manual secret:</p>
              <code className="block bg-brand-cream/60 border border-brand-line rounded p-2 break-all font-mono text-[11px]">{enroll.secret_manual}</code>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="text-xs text-amber-800 mb-2 font-medium">Save these recovery codes — shown only once:</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-[11px] font-mono">
              {enroll.recovery_codes.map((c) => (
                <div key={c} className="bg-white border border-amber-200 rounded px-1.5 py-1 text-center">{c}</div>
              ))}
            </div>
            <button type="button"
              onClick={() => { navigator.clipboard.writeText(enroll.recovery_codes.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              className="mt-2 text-xs text-amber-900 inline-flex items-center gap-1 hover:underline">
              <Copy className="w-3 h-3" /> {copied ? "Copied!" : "Copy all codes"}
            </button>
          </div>
          <input type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required
            className="input-field font-mono text-center tracking-widest"
            placeholder="Enter 6-digit code from authenticator"
            value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            data-testid="security-2fa-enroll-otp" />
          <div className="flex gap-2">
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="security-2fa-enroll-submit">
              {submitting ? "Enabling…" : "Enable 2FA"}
            </button>
            <button type="button" onClick={() => setEnroll(null)} className="btn-secondary">Cancel</button>
          </div>
        </form>
      ) : (
        <>
          <p className="text-sm text-brand-muted mb-3">
            Add an extra layer of security — sign-in will require a 6-digit code from your authenticator app.
          </p>
          <button onClick={startEnroll} disabled={submitting} className="btn-primary" data-testid="security-2fa-start">
            <KeyRound className="w-4 h-4 inline mr-1" /> {submitting ? "Generating QR…" : "Set up 2FA"}
          </button>
        </>
      )}
    </section>
  );
}

function SessionsCard({ onRevoke }) {
  const [submitting, setSubmitting] = useState(false);
  const click = async () => {
    if (!window.confirm("Sign out of every device, including this one? You'll need to sign in again.")) return;
    setSubmitting(true);
    try {
      await api.post("/auth/admin/sessions/revoke-all");
      localStorage.removeItem("sdf_token");
      toast.success("All sessions signed out.");
      setTimeout(() => { window.location.href = "/login"; }, 800);
      onRevoke?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };
  return (
    <section className="bg-white rounded-2xl border border-brand-line p-6" data-testid="security-sessions-card">
      <div className="flex items-center gap-2 mb-3">
        <LogOut className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
        <h2 className="font-serif text-xl text-brand-dark">Active sessions</h2>
      </div>
      <p className="text-sm text-brand-muted mb-3">
        Signs out every device using your account (including this one). Useful if you lost a phone or laptop.
      </p>
      <button onClick={click} disabled={submitting}
        className="btn-secondary border-amber-400 text-amber-700"
        data-testid="security-revoke-all">
        {submitting ? "Signing out…" : "Sign out everywhere"}
      </button>
    </section>
  );
}

function AuditCard({ items }) {
  return (
    <section className="bg-white rounded-2xl border border-brand-line p-6" data-testid="security-audit-card">
      <div className="flex items-center gap-2 mb-3">
        <Eye className="w-5 h-5 text-brand-chili" strokeWidth={1.6} />
        <h2 className="font-serif text-xl text-brand-dark">Recent security activity</h2>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-brand-muted">No activity yet.</p>
      ) : (
        <ul className="divide-y divide-brand-line text-xs" data-testid="security-audit-list">
          {items.slice(0, 50).map((e, i) => (
            <li key={`${e.at}-${i}`} className="py-2.5 flex justify-between gap-3">
              <div>
                <div className="font-medium text-brand-dark">{e.event}</div>
                <div className="text-brand-muted">{e.ip} · {(e.user_agent || "").slice(0, 60)}</div>
              </div>
              <div className="text-brand-muted whitespace-nowrap">{new Date(e.at).toLocaleString()}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * iter98 / Session E — Employee Admin management cards.
 * Super-admin-only surface. Employee-admin never sees this page
 * (AdminLayout redirects role='employee_admin' to /admin-orders).
 * ────────────────────────────────────────────────────────────*/
function EmployeeAdminActivityCard() {
  const [rows, setRows] = useState(null);
  const [emp, setEmp] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/employee-admin/security-activity?limit=50");
      setRows(data.events || []);
      setEmp(data.employee_email || "");
    } catch (err) {
      toast.error(formatApiError(err));
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div
      className="bg-white border border-brand-line rounded-2xl p-6"
      data-testid="employee-admin-activity-card"
    >
      <h2 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5" /> Employee Admin Recent Security Activity
      </h2>
      <p className="text-xs text-brand-muted mb-3">
        Independent audit trail for the employee admin account {emp && <>({emp})</>}.
      </p>
      {rows === null ? (
        <div className="text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-brand-muted">No security events recorded yet.</div>
      ) : (
        <div className="text-sm">
          {rows.map((e, i) => (
            <div
              key={i}
              className="flex items-start justify-between gap-3 py-2 border-b border-brand-line last:border-b-0"
              data-testid={`emp-activity-row-${i}`}
            >
              <div>
                <div className="font-medium text-brand-dark">{e.event}</div>
                <div className="text-xs text-brand-muted mt-0.5">
                  {e.ip ? `IP ${e.ip}` : ""}
                  {e.user_agent ? `${e.ip ? " · " : ""}${(e.user_agent || "").slice(0, 80)}` : ""}
                </div>
              </div>
              <div className="text-xs text-brand-muted whitespace-nowrap">
                {e.at ? new Date(e.at).toLocaleString("en-IN") : "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmployeeAdminChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/employee-admin/change-password", {
        current_password: current,
        new_password: next,
        confirm_new_password: confirm,
      });
      toast.success("Employee admin password updated. All employee sessions signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-brand-line rounded-2xl p-6 space-y-3"
      data-testid="emp-change-password-card"
    >
      <h2 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
        <Lock className="w-5 h-5" /> Change Employee Admin Password
      </h2>
      <p className="text-xs text-brand-muted">
        Updates the employee admin password immediately and invalidates any active employee sessions.
      </p>
      <input
        type="password" required autoComplete="off"
        value={current} onChange={(e) => setCurrent(e.target.value)}
        placeholder="Employee admin current password"
        className="input-field" data-testid="emp-current-password"
      />
      <input
        type="password" required autoComplete="new-password"
        value={next} onChange={(e) => setNext(e.target.value)}
        placeholder="New password"
        className="input-field" data-testid="emp-new-password"
      />
      <input
        type="password" required autoComplete="new-password"
        value={confirm} onChange={(e) => setConfirm(e.target.value)}
        placeholder="Re-enter new password"
        className="input-field" data-testid="emp-confirm-password"
      />
      <button
        type="submit" disabled={saving}
        className="btn-primary text-sm disabled:opacity-60"
        data-testid="emp-change-password-submit"
      >
        {saving ? "Updating…" : "Update Employee Admin Password"}
      </button>
    </form>
  );
}

function EmployeeAdminChangeEmailCard() {
  const [newEmail, setNewEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/employee-admin/change-email", {
        new_email: newEmail,
        current_password: current,
      });
      toast.success(data.message || "Employee admin email updated.");
      setNewEmail(""); setCurrent("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-brand-line rounded-2xl p-6 space-y-3"
      data-testid="emp-change-email-card"
    >
      <h2 className="font-serif text-xl text-brand-dark mb-1 flex items-center gap-2">
        <Mail className="w-5 h-5" /> Change Employee Admin Email
      </h2>
      <p className="text-xs text-brand-muted">
        Updates the employee admin login email immediately. The old email stops working and any
        active employee sessions are invalidated.
      </p>
      <input
        type="email" required
        value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
        placeholder="New employee admin email"
        className="input-field" data-testid="emp-new-email"
      />
      <input
        type="password" required autoComplete="off"
        value={current} onChange={(e) => setCurrent(e.target.value)}
        placeholder="Employee admin current password"
        className="input-field" data-testid="emp-email-current-password"
      />
      <button
        type="submit" disabled={saving}
        className="btn-primary text-sm disabled:opacity-60"
        data-testid="emp-change-email-submit"
      >
        {saving ? "Updating…" : "Update Employee Admin Email"}
      </button>
    </form>
  );
}

